import { EventBus } from '../core/eventBus';
import { Rng } from '../core/rng';
import type {
  BoardEventType,
  Coord,
  EnginePhase,
  LevelDef,
  ObjectiveProgress,
  PieceId,
  PieceKind,
  SpecialType,
} from '../core/types';
import { coordsEqual, isAdjacent } from '../core/types';
import {
  createGridFromLevel,
  fillBoard,
  Grid,
  makePiece,
  neighbors4,
} from './grid';
import {
  canSwapCells,
  findMatches,
  findValidMoves,
  hasAnyMatch,
  resolveMatchPlan,
  swapCreatesMatch,
} from './matchFinder';
// findMatches used in stabilizeInitial
import { activateSpecial, createSpecialPiece } from './specials';

export interface EngineSnapshot {
  phase: EnginePhase;
  movesLeft: number;
  score: number;
  cascadeDepth: number;
  objectives: ObjectiveProgress[];
  grid: Grid;
  selected: Coord | null;
  stars: number;
}

export interface StepVisual {
  type: BoardEventType;
  payload: Record<string, unknown>;
}

/**
 * Deterministic match-3 board engine.
 * Visual layer consumes events; never mutates grid.
 */
export class BoardEngine {
  readonly bus = new EventBus();
  level: LevelDef;
  grid: Grid;
  phase: EnginePhase = 'idle';
  movesLeft = 0;
  score = 0;
  cascadeDepth = 0;
  objectives: ObjectiveProgress[] = [];
  selected: Coord | null = null;
  private rng: Rng;
  private maxCascadeGuard = 64;
  private resolutionQueue: Array<() => void> = [];
  /** Last swap destination for special placement */
  private lastSwapTo: Coord | null = null;
  private specialsCreatedTotal = 0;
  private combosTotal = 0;
  private inputLocked = false;

  constructor(level: LevelDef, seedOffset = 0) {
    this.level = level;
    this.rng = new Rng(level.seed + seedOffset);
    this.grid = createGridFromLevel(level, this.rng);
    this.movesLeft = level.moves;
    this.objectives = level.objectives.map((o) => ({
      ...o,
      current: 0,
      done: false,
    }));
    // Clear any accidental initial matches
    this.stabilizeInitial();
  }

  private emit(type: BoardEventType, payload: Record<string, unknown> = {}): void {
    this.bus.emit(type, payload);
    this.bus.emit('board', { type, payload });
  }

  private setPhase(phase: EnginePhase): void {
    this.phase = phase;
    this.emit('phase', { phase });
  }

  get canInput(): boolean {
    return this.phase === 'idle' || this.phase === 'selected';
  }

  snapshot(): EngineSnapshot {
    return {
      phase: this.phase,
      movesLeft: this.movesLeft,
      score: this.score,
      cascadeDepth: this.cascadeDepth,
      objectives: this.objectives.map((o) => ({ ...o })),
      grid: this.grid,
      selected: this.selected,
      stars: this.computeStars(),
    };
  }

  select(coord: Coord): boolean {
    if (!this.canInput) return false;
    if (!this.grid.active(coord.r, coord.c)) return false;
    if (!this.grid.pieceAt(coord.r, coord.c)) return false;

    if (!this.selected) {
      this.selected = coord;
      this.setPhase('selected');
      return true;
    }

    if (coordsEqual(this.selected, coord)) {
      this.selected = null;
      this.setPhase('idle');
      return true;
    }

    if (isAdjacent(this.selected, coord)) {
      const a = this.selected;
      this.selected = null;
      return this.trySwap(a, coord);
    }

    // reselect
    this.selected = coord;
    this.setPhase('selected');
    return true;
  }

  trySwap(a: Coord, b: Coord): boolean {
    if (!this.canInput && this.phase !== 'selected') return false;
    if (!isAdjacent(a, b)) return false;
    if (!canSwapCells(this.grid, a, b)) {
      this.emit('swap_invalid', { a, b, reason: 'blocked' });
      this.selected = null;
      this.setPhase('idle');
      return false;
    }

    const pa = this.grid.pieceAt(a.r, a.c);
    const pb = this.grid.pieceAt(b.r, b.c);
    if (!pa || !pb) return false;

    // Special combinations / activation swaps
    const specialSwap = !!(pa.special || pb.special);
    const valid = specialSwap || swapCreatesMatch(this.grid, a, b);

    // Perform visual swap always, reverse if invalid
    this.setPhase('swapping');
    this.inputLocked = true;
    this.swapPieces(a, b);
    this.emit('swap', { a, b, valid });

    if (!valid) {
      // reverse
      this.swapPieces(a, b);
      this.emit('swap_invalid', { a, b, reason: 'no_match' });
      this.selected = null;
      this.setPhase('idle');
      this.inputLocked = false;
      return false;
    }

    this.movesLeft = Math.max(0, this.movesLeft - 1);
    this.lastSwapTo = b;
    this.cascadeDepth = 0;
    this.selected = null;

    // If both special or one special: activate
    if (pa.special && pb.special) {
      this.resolveSpecialCombo(b, pb, pa);
    } else if (pa.special || pb.special) {
      const specialCoord = pa.special ? b : a; // after swap, special moved
      // After swap: pa is at b, pb is at a
      const specialPiece = pa.special ? pa : pb;
      const specialAt = pa.special ? b : a;
      this.activateAndResolve(specialAt, specialPiece);
    } else {
      this.resolveMatchesAndCascades();
    }

    return true;
  }

  private swapPieces(a: Coord, b: Coord): void {
    const pa = this.grid.pieceAt(a.r, a.c);
    const pb = this.grid.pieceAt(b.r, b.c);
    this.grid.setPiece(a.r, a.c, pb);
    this.grid.setPiece(b.r, b.c, pa);
  }

  private resolveSpecialCombo(at: Coord, a: PieceId, b: PieceId): void {
    this.setPhase('special_activation');
    this.combosTotal++;
    const result = activateSpecial(this.grid, at, a, b);
    this.emit('special_combo', {
      at,
      label: result.label,
      specials: [a.special, b.special],
    });
    if (result.convertToSpecial?.length) {
      for (const conv of result.convertToSpecial) {
        const existing = this.grid.pieceAt(conv.at.r, conv.at.c);
        if (existing) {
          this.grid.setPiece(
            conv.at.r,
            conv.at.c,
            makePiece(conv.kind, createSpecialPiece(conv.kind, conv.special, conv.beamAxis))
          );
          this.emit('special_created', { at: conv.at, special: conv.special, kind: conv.kind });
        }
      }
      // Detonate converted bursts
      const extra: Coord[] = [...result.clearCells];
      for (const conv of result.convertToSpecial) {
        const p = this.grid.pieceAt(conv.at.r, conv.at.c);
        if (p?.special) {
          const act = activateSpecial(this.grid, conv.at, p);
          extra.push(...act.clearCells);
        }
      }
      this.clearCells(extra, result.scoreBonus);
    } else {
      this.clearCells(result.clearCells, result.scoreBonus);
    }
    this.afterClear();
  }

  private activateAndResolve(at: Coord, piece: PieceId): void {
    this.setPhase('special_activation');
    const result = activateSpecial(this.grid, at, piece);
    this.emit('special_activated', {
      at,
      special: piece.special,
      label: result.label,
    });
    this.clearCells(result.clearCells, result.scoreBonus);
    this.afterClear();
  }

  private resolveMatchesAndCascades(): void {
    this.setPhase('resolving');
    let guard = 0;
    while (guard++ < this.maxCascadeGuard) {
      const groups = findMatches(this.grid);
      if (groups.length === 0) break;

      this.cascadeDepth = guard;
      this.emit('cascade', { depth: this.cascadeDepth });
      this.emit('match', {
        groups: groups.map((g) => ({
          cells: g.cells,
          kind: g.kind,
          pattern: g.pattern,
          special: g.specialToCreate,
        })),
      });

      const plan = resolveMatchPlan(groups, this.lastSwapTo ?? undefined);
      // Don't clear cells that will become specials — replace instead
      const specialKeys = new Set(plan.specials.map((s) => `${s.at.r},${s.at.c}`));

      // Existing specials caught in a match activate (extra clears)
      const chainedClear: Coord[] = [];
      for (const c of plan.clearCells) {
        if (specialKeys.has(`${c.r},${c.c}`)) continue;
        const p = this.grid.pieceAt(c.r, c.c);
        if (p?.special) {
          const act = activateSpecial(this.grid, c, p);
          this.emit('special_activated', {
            at: c,
            special: p.special,
            label: act.label,
          });
          chainedClear.push(...act.clearCells);
          this.score += act.scoreBonus;
        }
      }

      const clearSet = new Map<string, Coord>();
      for (const c of [...plan.clearCells, ...chainedClear]) {
        if (!specialKeys.has(`${c.r},${c.c}`)) clearSet.set(`${c.r},${c.c}`, c);
      }
      const toClear = [...clearSet.values()];

      // Count collections before clear
      this.tallyClears(toClear);

      const scoreGain = toClear.length * 10 * this.cascadeDepth + plan.specials.length * 50;
      this.score += scoreGain;
      this.emit('score', { score: this.score, gain: scoreGain });

      // Clear non-special cells
      for (const c of toClear) {
        this.damageCell(c, true);
      }

      // Place specials
      for (const s of plan.specials) {
        const data = createSpecialPiece(s.kind, s.special, s.beamAxis);
        this.grid.setPiece(s.at.r, s.at.c, makePiece(s.kind, data));
        this.specialsCreatedTotal++;
        this.emit('special_created', {
          at: s.at,
          special: s.special,
          kind: s.kind,
          beamAxis: s.beamAxis,
        });
        this.trackSpecialCreated(s.special);
      }

      // Damage under tiles on matched cells + adjacent solid blockers (crates/seals)
      for (const c of plan.clearCells) {
        this.damageUnder(c);
        this.damageAdjacentBlockers(c);
      }

      this.applyGravityAndSpawn();
      this.lastSwapTo = null; // only first match uses swap position

      // Check for specials that got matched while sitting (activate them)
      // Standard: specials clear with match groups already handled via placement
    }

    this.tickHazards();
    this.checkRelics();
    this.finalizeTurn();
  }

  private afterClear(): void {
    this.applyGravityAndSpawn();
    // Continue cascades from natural matches
    this.resolveMatchesAndCascades();
  }

  private clearCells(cells: Coord[], bonus = 0): void {
    const uniq = new Map<string, Coord>();
    for (const c of cells) uniq.set(`${c.r},${c.c}`, c);
    const list = [...uniq.values()];
    this.tallyClears(list);

    // Chain-react specials in clear set
    const chained: Coord[] = [];
    for (const c of list) {
      const p = this.grid.pieceAt(c.r, c.c);
      if (p?.special) {
        // avoid re-activating the same special infinitely: clear without re-expand here
        // already expanded by activateSpecial
      }
      this.damageCell(c, true);
      chained.push(c);
    }

    const gain = list.length * 12 + bonus;
    this.score += gain;
    this.emit('clear', { cells: list });
    this.emit('score', { score: this.score, gain });
    for (const c of list) this.damageUnder(c);
  }

  private damageCell(c: Coord, removePiece: boolean): void {
    const cell = this.grid.get(c.r, c.c);
    if (!cell?.active) return;

    if (cell.blocker) {
      cell.blocker.layers -= 1;
      this.emit('blocker_damaged', {
        at: c,
        type: cell.blocker.type,
        layersLeft: cell.blocker.layers,
      });
      if (cell.blocker.type === 'frost') this.progressObjective('clear_frost', 1);
      if (cell.blocker.type === 'crate') this.progressObjective('clear_crate', 1);
      if (cell.blocker.type === 'vine') this.progressObjective('clear_vine', 1);
      if (cell.blocker.layers <= 0) {
        if (cell.blocker.type === 'seal') {
          cell.sealed = false;
          this.progressObjective('clear_seals', 1);
        }
        cell.blocker = null;
      } else if (cell.blocker.type === 'crate' || cell.blocker.type === 'seal') {
        // crates block piece removal until gone
        return;
      }
    }

    if (cell.sealed) {
      cell.sealed = false;
      this.progressObjective('clear_seals', 1);
      return;
    }

    if (removePiece) {
      cell.piece = null;
    }
  }

  private damageUnder(c: Coord): void {
    const cell = this.grid.get(c.r, c.c);
    if (!cell?.under) return;
    cell.under.layers -= 1;
    if (cell.under.layers <= 0) {
      cell.under = null;
      this.emit('under_cleared', { at: c });
      this.progressObjective('clear_tiles', 1);
    }
  }

  /** Matches damage neighboring crates / seals (classic match-3 adjacency). */
  private damageAdjacentBlockers(c: Coord): void {
    for (const n of neighbors4(c.r, c.c)) {
      const cell = this.grid.get(n.r, n.c);
      if (!cell?.active) continue;
      if (cell.blocker?.type === 'crate' || cell.blocker?.type === 'seal' || cell.sealed) {
        this.damageCell(n, false);
      }
    }
  }

  private tallyClears(cells: Coord[]): void {
    for (const c of cells) {
      const p = this.grid.pieceAt(c.r, c.c);
      if (!p) continue;
      this.progressCollect(p.kind, 1);
    }
  }

  private progressCollect(kind: PieceKind, n: number): void {
    for (const o of this.objectives) {
      if (o.type === 'collect' && o.kind === kind && !o.done) {
        o.current = Math.min(o.target, o.current + n);
        if (o.current >= o.target) o.done = true;
        this.emit('objective_progress', { objective: { ...o } });
      }
    }
  }

  private trackSpecialCreated(special: SpecialType): void {
    for (const o of this.objectives) {
      if (o.type === 'activate_specials' && !o.done) {
        // count creations toward activate if no special filter, or filter match on later activation
        if (!o.special || o.special === special) {
          o.current = Math.min(o.target, o.current + 1);
          if (o.current >= o.target) o.done = true;
          this.emit('objective_progress', { objective: { ...o } });
        }
      }
      if (o.type === 'combo_specials' && !o.done) {
        // handled in combo
      }
    }
  }

  private progressObjective(type: ObjectiveProgress['type'], n: number): void {
    for (const o of this.objectives) {
      if (o.type === type && !o.done) {
        o.current = Math.min(o.target, o.current + n);
        if (o.current >= o.target) o.done = true;
        this.emit('objective_progress', { objective: { ...o } });
      }
    }
    // combo tracking
    if (type === 'activate_specials') {
      /* no-op */
    }
  }

  private applyGravityAndSpawn(): void {
    this.setPhase('spawning');
    const falls: Array<{ from: Coord; to: Coord; pieceId: number }> = [];
    const spawns: Array<{ at: Coord; piece: PieceId }> = [];

    for (let c = 0; c < this.grid.cols; c++) {
      // Segment columns by holes and solid crates (pieces cannot fall through).
      const segments: { slots: number[]; pieces: PieceId[] }[] = [];
      let curSlots: number[] = [];
      let curPieces: PieceId[] = [];

      for (let r = this.grid.rows - 1; r >= 0; r--) {
        const cell = this.grid.get(r, c)!;
        if (!cell.active || cell.blocker?.type === 'crate') {
          if (curSlots.length) {
            segments.push({ slots: curSlots, pieces: curPieces });
            curSlots = [];
            curPieces = [];
          }
          continue;
        }
        curSlots.push(r);
        if (cell.piece) {
          curPieces.push(cell.piece);
          cell.piece = null;
        }
      }
      if (curSlots.length) segments.push({ slots: curSlots, pieces: curPieces });

      for (const seg of segments) {
        let pi = 0;
        for (let i = 0; i < seg.slots.length; i++) {
          const r = seg.slots[i]!;
          if (pi < seg.pieces.length) {
            const piece = seg.pieces[pi++]!;
            this.grid.setPiece(r, c, piece);
            falls.push({ from: { r, c }, to: { r, c }, pieceId: piece.id });
          }
        }
      }
    }

    // Spawn into remaining empty active cells (top-down fill).
    for (let c = 0; c < this.grid.cols; c++) {
      for (let r = 0; r < this.grid.rows; r++) {
        const cell = this.grid.get(r, c);
        if (!cell?.active) continue;
        if (cell.blocker?.type === 'crate') continue;
        if (!cell.piece && cell.spawnAllowed) {
          const kind = this.pickSpawnKind();
          const piece = makePiece(kind);
          if (
            this.level.objectives.some((o) => o.type === 'drop') &&
            this.rng.next() < 0.04
          ) {
            piece.isRelic = true;
          }
          cell.piece = piece;
          spawns.push({ at: { r, c }, piece });
        }
      }
    }

    // Portal transfer when destination is empty.
    this.grid.forEachActive((r, c, cell) => {
      if (cell.portalTo && cell.piece) {
        const dest = this.grid.get(cell.portalTo.r, cell.portalTo.c);
        if (dest?.active && !dest.piece) {
          dest.piece = cell.piece;
          cell.piece = null;
        }
      }
    });

    if (falls.length) this.emit('fall', { falls });
    if (spawns.length) {
      this.emit('spawn', {
        spawns: spawns.map((s) => ({ at: s.at, kind: s.piece.kind, id: s.piece.id })),
      });
    }
  }

  private pickSpawnKind(): PieceKind {
    const set = this.level.pieceSet;
    const weights = set.map((k) => this.level.spawnWeights?.[k] ?? 1);
    return this.rng.weighted(set, weights);
  }

  private tickHazards(): void {
    // Vine spread: expand to one adjacent empty-of-vine cell if not cleared
    let vineCount = 0;
    this.grid.forEachActive((_r, _c, cell) => {
      if (cell.blocker?.type === 'vine') vineCount++;
    });
    if (vineCount > 0 && this.rng.next() < 0.35) {
      const candidates: Coord[] = [];
      this.grid.forEachActive((r, c, cell) => {
        if (cell.blocker?.type === 'vine') {
          for (const n of neighbors4(r, c)) {
            const nc = this.grid.get(n.r, n.c);
            if (nc?.active && !nc.blocker && nc.piece) {
              candidates.push(n);
            }
          }
        }
      });
      if (candidates.length) {
        const pick = this.rng.pick(candidates);
        const cell = this.grid.get(pick.r, pick.c)!;
        cell.blocker = { type: 'vine', layers: 1 };
        this.emit('vine_spread', { at: pick });
      }
    }

    // Rift tick
    let rifts = 0;
    this.grid.forEachActive((_r, _c, cell) => {
      if (cell.blocker?.type === 'rift') rifts++;
    });
    if (rifts > 0) {
      this.emit('rift_tick', { count: rifts });
      // survive objective: stay under threshold
      for (const o of this.objectives) {
        if (o.type === 'survive_rift' && !o.done) {
          // target = max rifts allowed; done if we finish level with rifts <= target... 
          // interpret as: reduce rifts to 0 or keep below target
          o.current = Math.max(0, o.target - rifts);
          if (rifts <= 0) {
            o.current = o.target;
            o.done = true;
          }
          this.emit('objective_progress', { objective: { ...o } });
        }
      }
    }

    // Conveyor move pieces
    const moves: Array<{ from: Coord; to: Coord }> = [];
    const snapshot: Array<{ r: number; c: number; piece: PieceId | null }> = [];
    this.grid.forEachActive((r, c, cell) => {
      if (cell.conveyorDir && cell.piece) {
        snapshot.push({ r, c, piece: cell.piece });
      }
    });
    // clear conveyor pieces then place
    for (const s of snapshot) {
      this.grid.setPiece(s.r, s.c, null);
    }
    for (const s of snapshot) {
      const cell = this.grid.get(s.r, s.c)!;
      const dir = cell.conveyorDir!;
      let nr = s.r;
      let nc = s.c;
      if (dir === 'up') nr--;
      if (dir === 'down') nr++;
      if (dir === 'left') nc--;
      if (dir === 'right') nc++;
      const dest = this.grid.get(nr, nc);
      if (dest?.active && !dest.piece && dest.conveyorDir) {
        dest.piece = s.piece;
        moves.push({ from: { r: s.r, c: s.c }, to: { r: nr, c: nc } });
      } else {
        // bounce back
        this.grid.setPiece(s.r, s.c, s.piece);
      }
    }
  }

  private checkRelics(): void {
    let dropped = 0;
    for (let c = 0; c < this.grid.cols; c++) {
      const cell = this.grid.get(this.grid.rows - 1, c);
      if (cell?.active && cell.isExit && cell.piece?.isRelic) {
        cell.piece = null;
        dropped++;
      }
    }
    if (dropped > 0) {
      this.emit('relic_dropped', { count: dropped });
      for (const o of this.objectives) {
        if (o.type === 'drop' && !o.done) {
          o.current = Math.min(o.target, o.current + dropped);
          if (o.current >= o.target) o.done = true;
          this.emit('objective_progress', { objective: { ...o } });
        }
      }
      this.applyGravityAndSpawn();
    }
  }

  private finalizeTurn(): void {
    // Score objective
    for (const o of this.objectives) {
      if (o.type === 'score' && !o.done) {
        o.current = Math.min(o.target, this.score);
        if (o.current >= o.target) o.done = true;
        this.emit('objective_progress', { objective: { ...o } });
      }
      if (o.type === 'combo_specials' && !o.done) {
        o.current = Math.min(o.target, this.combosTotal);
        if (o.current >= o.target) o.done = true;
      }
    }

    if (this.objectives.every((o) => o.done)) {
      this.setPhase('won');
      this.emit('win', {
        score: this.score,
        movesLeft: this.movesLeft,
        stars: this.computeStars(),
      });
      this.inputLocked = false;
      return;
    }

    if (this.movesLeft <= 0) {
      this.setPhase('lost');
      this.emit('lose', { score: this.score });
      this.inputLocked = false;
      return;
    }

    // No moves => reshuffle
    const moves = findValidMoves(this.grid);
    if (moves.length === 0) {
      this.reshuffle();
    }

    this.setPhase('idle');
    this.inputLocked = false;
  }

  reshuffle(): void {
    this.setPhase('reshuffling');
    this.emit('reshuffle', {});
    const pieces: PieceId[] = [];
    this.grid.forEachActive((_r, _c, cell) => {
      if (cell.piece && !cell.piece.special && !cell.piece.isRelic) {
        pieces.push(cell.piece);
        cell.piece = null;
      }
    });
    const shuffled = this.rng.shuffle(pieces);
    let i = 0;
    // Try placements without matches
    const empties: Coord[] = [];
    this.grid.forEachActive((r, c, cell) => {
      if (!cell.piece && !cell.blocker?.type) empties.push({ r, c });
    });
    // Only fill cells we cleared
    this.grid.forEachActive((r, c, cell) => {
      if (!cell.piece && i < shuffled.length) {
        // skip crates
        if (cell.blocker?.type === 'crate') return;
        cell.piece = shuffled[i++]!;
      }
    });
    // If still matches, shuffle again up to N times
    let attempts = 0;
    while ((hasAnyMatch(this.grid) || findValidMoves(this.grid).length === 0) && attempts < 30) {
      const pool: PieceId[] = [];
      this.grid.forEachActive((_r, _c, cell) => {
        if (cell.piece && !cell.piece.special && !cell.piece.isRelic) {
          pool.push(cell.piece);
          cell.piece = null;
        }
      });
      const sh = this.rng.shuffle(pool);
      let j = 0;
      this.grid.forEachActive((_r, _c, cell) => {
        if (!cell.piece && j < sh.length && cell.blocker?.type !== 'crate') {
          cell.piece = sh[j++]!;
        }
      });
      attempts++;
    }
    // Ensure valid moves exist by forcing a known pattern if needed
    if (findValidMoves(this.grid).length === 0) {
      this.forceValidMove();
    }
  }

  private forceValidMove(): void {
    // Find three cells to set up a simple match opportunity
    for (let r = 0; r < this.grid.rows; r++) {
      for (let c = 0; c < this.grid.cols - 3; c++) {
        const cells = [c, c + 1, c + 2, c + 3].map((cc) => this.grid.get(r, cc));
        if (cells.every((cell) => cell?.active && cell.piece && !cell.piece.special)) {
          const kind = cells[0]!.piece!.kind;
          cells[1]!.piece!.kind = kind;
          cells[3]!.piece!.kind = kind;
          // cells 0,1,3 same; 2 different => swap 2 with 3? actually 0,1 and need adjacent
          // Set c, c+1 same, c+3 same, c+2 different — swap c+2 with c+3 creates match
          const other = this.level.pieceSet.find((k) => k !== kind) ?? kind;
          cells[2]!.piece!.kind = other;
          return;
        }
      }
    }
  }

  private stabilizeInitial(): void {
    let guard = 0;
    while (hasAnyMatch(this.grid) && guard++ < 50) {
      // Break matches by recoloring the last cell of each group.
      const groups = findMatches(this.grid);
      for (const g of groups) {
        const cell = g.cells[g.cells.length - 1]!;
        const piece = this.grid.pieceAt(cell.r, cell.c);
        if (!piece || piece.special || piece.isRelic) continue;
        const alternatives = this.level.pieceSet.filter((k) => k !== piece.kind);
        if (alternatives.length) {
          piece.kind = this.rng.pick(alternatives);
        }
      }
      if (groups.length === 0) break;
    }
    // Final safety: full recolor pass without creating lines of 3.
    if (hasAnyMatch(this.grid)) {
      this.grid.forEachActive((r, c, cell) => {
        if (!cell.piece || cell.piece.special || cell.piece.isRelic) return;
        for (let attempt = 0; attempt < 24; attempt++) {
          const kind = this.pickSpawnKind();
          cell.piece.kind = kind;
          // temporary check using neighbors only
          if (!this.createsLineAt(r, c, kind)) break;
        }
      });
    }
    if (findValidMoves(this.grid).length === 0) {
      this.forceValidMove();
    }
  }

  private createsLineAt(r: number, c: number, kind: import('../core/types').PieceKind): boolean {
    let h = 1;
    for (let cc = c - 1; cc >= 0; cc--) {
      const p = this.grid.pieceAt(r, cc);
      if (p?.kind === kind) h++;
      else break;
    }
    for (let cc = c + 1; cc < this.grid.cols; cc++) {
      const p = this.grid.pieceAt(r, cc);
      if (p?.kind === kind) h++;
      else break;
    }
    if (h >= 3) return true;
    let v = 1;
    for (let rr = r - 1; rr >= 0; rr--) {
      const p = this.grid.pieceAt(rr, c);
      if (p?.kind === kind) v++;
      else break;
    }
    for (let rr = r + 1; rr < this.grid.rows; rr++) {
      const p = this.grid.pieceAt(rr, c);
      if (p?.kind === kind) v++;
      else break;
    }
    return v >= 3;
  }

  computeStars(): number {
    const [s1, s2, s3] = this.level.starScores ?? [
      Math.floor(this.level.moves * 50),
      Math.floor(this.level.moves * 100),
      Math.floor(this.level.moves * 160),
    ];
    if (!this.objectives.every((o) => o.done)) return 0;
    let stars = 1;
    if (this.score >= s1) stars = 1;
    if (this.score >= s2 || this.movesLeft >= 3) stars = 2;
    if (this.score >= s3 || this.movesLeft >= 6) stars = 3;
    // efficiency bonus
    if (this.movesLeft >= Math.floor(this.level.moves * 0.35)) stars = Math.max(stars, 2);
    if (this.movesLeft >= Math.floor(this.level.moves * 0.5)) stars = 3;
    return Math.min(3, stars);
  }

  /** Hint: return a valid move if hints enabled */
  getHint(): { a: Coord; b: Coord } | null {
    const moves = findValidMoves(this.grid);
    return moves[0] ?? null;
  }

  getValidMoveCount(): number {
    return findValidMoves(this.grid).length;
  }

  /** Autoplay one random valid move — for simulation */
  autoMove(): boolean {
    if (!this.canInput) return false;
    const moves = findValidMoves(this.grid);
    if (!moves.length) {
      this.reshuffle();
      return this.autoMove();
    }
    const m = this.rng.pick(moves);
    return this.trySwap(m.a, m.b);
  }

  /** Headless play until win/lose */
  simulateToEnd(maxMoves = 500): 'won' | 'lost' {
    let steps = 0;
    while (this.phase !== 'won' && this.phase !== 'lost' && steps++ < maxMoves) {
      if (this.phase === 'idle' || this.phase === 'selected') {
        if (!this.autoMove()) break;
      } else {
        break;
      }
    }
    if (this.phase === 'won') return 'won';
    return 'lost';
  }
}
